// The website's error lane, reduced to what can be read without reading a
// visitor. src/lib/analytics.ts hands every outgoing event to redactException
// as posthog-js's `before_send`; everything that is not a `$exception` passes
// through untouched.
//
// The site holds no patient data, but an error message is free text, and the
// analytics contract refuses free text that could name a visitor — a failed
// waitlist request can echo the address that was typed. So a `$exception`
// leaves with:
//   · each exception's type, and its message with every quoted span, email
//     address and run of six or more digits replaced, cut to 200 characters;
//   · stack frames reduced to file (no query string, no hash), function, line,
//     column, in_app, platform and chunk id — no source lines, no variables;
//   · `$exception_level`. Every other `$exception_*` key is dropped.
//
// stripAdClickIds runs first, on every event: posthog-js's
// `mask_personal_data_properties` masks an ad click id's VALUE and keeps the
// parameter (`?fbclid=<masked>`), so one page read as many `$current_url`s. The
// parameters are removed from the URL properties outright (E-488).
//
// No imports, and no TypeScript that needs more than stripping, so
// __tests__/exception-redaction.test.mjs loads it straight through Node (the
// referrer-class.ts pattern) and posthog-js keeps exactly one importer.

type Props = Record<string, unknown>;

export interface RedactableEvent {
  event: string;
  properties?: Props;
}

export const MAX_EXCEPTIONS = 5;
export const MAX_FRAMES = 50;
export const MAX_MESSAGE_CHARS = 200;

const EXCEPTION_TYPE = /^[A-Za-z_$][\w$.]{0,80}$/;
const FRAME_FUNCTION = /^[\w$.<> ]{1,120}$/;
const CHUNK_ID = /^[\w-]{1,100}$/;
const MECHANISM_TYPE = /^[a-z_]{1,40}$/;
const PLATFORM = /^[a-z:]{1,40}$/;
const EXCEPTION_LEVELS = new Set([
  "fatal",
  "error",
  "warning",
  "log",
  "info",
  "debug",
]);

const QUOTED = /(["'`])(?:(?!\1)[^\n]){1,200}\1/g;
const EMAIL = /[^\s@"'`<>()[\]]+@[^\s@"'`<>()[\]]+\.[^\s@"'`<>()[\]]+/g;
const LONG_NUMBER = /\d{6,}/g;

function isRecord(value: unknown): value is Props {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/** A message with the spans that could carry typed or personal text removed. */
export function redactMessage(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value
    .replace(QUOTED, "$1…$1")
    .replace(EMAIL, "[email]")
    .replace(LONG_NUMBER, "[number]")
    .slice(0, MAX_MESSAGE_CHARS);
}

function withoutQuery(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  const cut = value.search(/[?#]/);
  return (cut === -1 ? value : value.slice(0, cut)).slice(0, 300);
}

function redactFrame(frame: unknown): Props | null {
  if (!isRecord(frame)) return null;
  const out: Props = {};
  const filename = withoutQuery(frame.filename);
  if (filename) out.filename = filename;
  const absPath = withoutQuery(frame.abs_path);
  if (absPath) out.abs_path = absPath;
  if (
    typeof frame.function === "string" &&
    FRAME_FUNCTION.test(frame.function)
  ) {
    out.function = frame.function;
  }
  if (isCount(frame.lineno)) out.lineno = frame.lineno;
  if (isCount(frame.colno)) out.colno = frame.colno;
  if (typeof frame.in_app === "boolean") out.in_app = frame.in_app;
  if (typeof frame.platform === "string" && PLATFORM.test(frame.platform)) {
    out.platform = frame.platform;
  }
  if (typeof frame.chunk_id === "string" && CHUNK_ID.test(frame.chunk_id)) {
    out.chunk_id = frame.chunk_id;
  }
  return out;
}

function redactOne(exception: unknown): Props {
  const ex = isRecord(exception) ? exception : {};
  const out: Props = {
    type:
      typeof ex.type === "string" && EXCEPTION_TYPE.test(ex.type)
        ? ex.type
        : "Error",
  };
  const value = redactMessage(ex.value);
  if (value !== undefined) out.value = value;

  if (isRecord(ex.mechanism)) {
    const mechanism: Props = {};
    const { handled, synthetic, type } = ex.mechanism;
    if (typeof handled === "boolean") mechanism.handled = handled;
    if (typeof synthetic === "boolean") mechanism.synthetic = synthetic;
    if (typeof type === "string" && MECHANISM_TYPE.test(type)) {
      mechanism.type = type;
    }
    out.mechanism = mechanism;
  }

  const raw =
    isRecord(ex.stacktrace) && Array.isArray(ex.stacktrace.frames)
      ? ex.stacktrace.frames
      : [];
  // posthog-js orders frames outermost first, so the throw site is the LAST
  // one — keep that end of a long stack.
  const frames = raw
    .map(redactFrame)
    .filter((f): f is Props => f !== null)
    .slice(-MAX_FRAMES);
  if (frames.length > 0) out.stacktrace = { type: "raw", frames };
  return out;
}

/** posthog-js `before_send`: a `$exception` is rebuilt, anything else passes. */
export function redactException<T extends RedactableEvent>(
  event: T | null,
): T | null {
  if (!event || event.event !== "$exception") return event;
  const props = event.properties ?? {};
  const out: Props = {};
  for (const [key, value] of Object.entries(props)) {
    if (!key.startsWith("$exception")) out[key] = value;
  }
  const list = Array.isArray(props.$exception_list)
    ? props.$exception_list
    : [];
  out.$exception_list = list.slice(0, MAX_EXCEPTIONS).map(redactOne);
  const level = props.$exception_level;
  if (typeof level === "string" && EXCEPTION_LEVELS.has(level)) {
    out.$exception_level = level;
  }
  return { ...event, properties: out };
}

/**
 * The ad click ids posthog-js masks under `mask_personal_data_properties` (its
 * PERSONAL_DATA_CAMPAIGN_PARAMS). Each names one ad click, so a URL carrying
 * one is a value nobody else shares.
 */
export const AD_CLICK_ID_PARAMS: readonly string[] = [
  "gclid",
  "gclsrc",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "igshid",
  "ttclid",
  "rdt_cid",
  "epik",
  "qclid",
  "sccid",
  "irclid",
  "_kx",
];

const AD_CLICK_IDS = new Set(AD_CLICK_ID_PARAMS);

/** The URL properties posthog-js records on an event. */
const URL_PROPERTIES = [
  "$current_url",
  "$session_entry_url",
  "$initial_current_url",
] as const;

function paramName(pair: string): string {
  const key = pair.split("=")[0] ?? "";
  try {
    return decodeURIComponent(key.replace(/\+/g, " "));
  } catch {
    return key;
  }
}

/**
 * A URL without its ad click id parameters. Everything else (path, the other
 * parameters in their order, the hash) is kept byte for byte; a URL with none
 * comes back as the same string.
 */
export function stripAdClickIdsFromUrl(url: string): string {
  const hashAt = url.indexOf("#");
  const head = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const q = head.indexOf("?");
  if (q === -1) return url;
  const pairs = head.slice(q + 1).split("&");
  const kept = pairs.filter((pair) => !AD_CLICK_IDS.has(paramName(pair)));
  if (kept.length === pairs.length) return url;
  const query = kept.filter((pair) => pair !== "").join("&");
  return `${head.slice(0, q)}${query ? `?${query}` : ""}${hash}`;
}

/** posthog-js `before_send`, first step: URL properties lose ad click ids. */
export function stripAdClickIds<T extends RedactableEvent>(
  event: T | null,
): T | null {
  if (!event || !event.properties) return event;
  let properties: Props | null = null;
  for (const key of URL_PROPERTIES) {
    const value = event.properties[key];
    if (typeof value !== "string") continue;
    const stripped = stripAdClickIdsFromUrl(value);
    if (stripped === value) continue;
    properties ??= { ...event.properties };
    properties[key] = stripped;
  }
  return properties === null ? event : { ...event, properties };
}
